import { Component, effect, EventEmitter, inject, Input, Output, signal } from '@angular/core'
import { MatTooltipModule } from '@angular/material/tooltip'
import { DEV_OR_LOCALHOST } from '@bases/base.token'
import { CMInput, CMLayerInput } from '@core/models/calculation-module.model'
import { CalculationModuleService } from '@core/services/calculation-module.service'
import { ToasterService } from '@core/services/toaster.service'
import { fileTypeFromBuffer } from 'file-type'
import { BehaviorSubject } from 'rxjs'

@Component({
  selector: 'app-cm-input',
  templateUrl: './cm-input.component.html',
  styleUrl: './cm-input.component.scss',
  imports: [
    //Material
    MatTooltipModule,
  ],
})
export class CmInputComponent {
  @Input({ required: true }) cmInput!: CMInput | CMLayerInput
  // For dynamic inputs
  @Input() index = 0
  @Input() isInputsArrayTemplate = false
  @Input() isInputOptionsParentInput = false
  @Output() inputValueChangedNotifier = new EventEmitter<any>()
  @Output() layerInputValueChangedNotifier = new EventEmitter<any>()

  devOrLocalhost = inject<BehaviorSubject<string>>(DEV_OR_LOCALHOST)
  calculationModuleService = inject(CalculationModuleService)
  private readonly _toasterService = inject(ToasterService)

  fileError = signal<string | null>(null)

  //@todo, define enum?
  type_select = 'select'
  type_input = 'input'
  type_radio = 'radio'
  type_range = 'range'
  type_date = `datetime-local`
  type_checkbox = 'checkbox'
  type_layer = 'layer'
  type_file = 'file'

  // File validation
  readonly MAX_FILE_SIZE_BYTES = 12 * 1024 * 1024

  constructor() {
    effect(() => {
      if (this.fileError() != null) {
        this._toasterService.openSnackBarError(this.fileError() ?? 'No specific Error.')
      }
    })
  }

  getFileAcceptAttr(): string {
    const format = (this.cmInput as CMInput)?.input_format
    if (format === 'json') return 'application/json,.json'
    if (format === 'csv') return 'text/csv,.csv'
    if (format === 'txt') return 'text/plain,.txt'
    return '.csv,.json,application/json,text/csv'
  }

  getFileHintLabel(): string {
    return `Expected format: ${(this.cmInput as CMInput)?.input_format}; Max size: ${this.MAX_FILE_SIZE_BYTES / (1024 * 1024)} Mb.`
  }

  async onFileChange(event: Event) {
    this.fileError.set(null)
    const inputElementTarget = event.target as HTMLInputElement

    try {
      const file: File | undefined = inputElementTarget.files?.[0]
      if (!file) {
        return
      }

      // Size check
      if (file.size <= 1) {
        throw new Error('Empty file.')
      }
      if (file.size > this.MAX_FILE_SIZE_BYTES) {
        throw new Error(
          `File too big (${(file.size / (1024 * 1024)).toFixed(2)} Mb). Limit: ${this.MAX_FILE_SIZE_BYTES / (1024 * 1024)} Mb.`,
        )
      }

      // Read
      const buffer = await file.arrayBuffer()

      // Sniffing — blocks renamed binary formats (exe, zip, pdf, etc.)
      const detectedType = await fileTypeFromBuffer(buffer)
      if (detectedType !== undefined) throw new Error('Invalid file type.')

      // Parse
      const text = new TextDecoder().decode(buffer)

      this.updateInput(text)
    } catch (e) {
      inputElementTarget.value = '' // Clear the file input on validation failure
      this.fileError.set(e instanceof Error ? e.message : 'Unexpected error.')
    }
  }

  onRadioOrSelectInputChange(event: Event) {
    const target = event.target as HTMLInputElement
    const value = target.value
    // Handle parent input of DynamicCMInputOptions in DynamicCMInputOptions
    if (this.isInputOptionsParentInput) {
      this.inputValueChangedNotifier.emit({ value: value, input: this.cmInput })
      return
    }

    if (this.cmInput) {
      this.updateInput(value)
    }
  }

  setLayerNeeded(layerInput: CMLayerInput, event: any) {
    const selectedLayerName = event.target.value
    // Handle parent input of DynamicCMInputOptions in DynamicCMInputOptions
    if (this.isInputOptionsParentInput) {
      this.layerInputValueChangedNotifier.emit({
        layerInput: layerInput,
        selectedLayerName: selectedLayerName,
      })
      return
    }

    this.calculationModuleService.setLayerNeeded(layerInput, event)
  }

  onInputOrRangeInputChange(event: Event) {
    const input = event.target as HTMLInputElement
    const newValue = input.value

    const current = this.cmInput
    if (!current) return

    const min = +current.input_min
    const max = +current.input_max
    const numericValue = +newValue

    if (numericValue >= min && numericValue <= max) {
      this.updateInput(newValue)
    } else {
      // Reset to previous valid value
      input.value = current.input_value.toString()
    }
  }

  onCheckboxChange(event: Event) {
    const inputElement = event.target as HTMLInputElement
    const newValue = inputElement.value

    let currentValues: (string | number)[] = Array.isArray(this.cmInput.input_value)
      ? [...this.cmInput.input_value]
      : []

    if (inputElement.checked) {
      if (!currentValues.map((value) => String(value)).includes(newValue)) {
        currentValues.push(newValue)
      }
    } else {
      currentValues = currentValues.filter((value) => String(value) !== newValue)
    }

    this.updateInput(currentValues)
  }

  isChecked(value: string | number): boolean {
    const currentValues = (this.cmInput?.input_value || []) as (string | number)[]
    return currentValues.includes(String(value))
  }

  onDateInputChange(newValue: string): void {
    if (!this.cmInput.input_min && !this.cmInput.input_max) {
      this.updateInput(newValue)
    } else {
      const minDate = new Date(this.cmInput.input_min)
      const maxDate = new Date(this.cmInput.input_max)
      const valueDate = new Date(newValue)

      if (!isNaN(valueDate.getTime()) && valueDate >= minDate && valueDate <= maxDate) {
        this.updateInput(newValue)
      } else {
        // Optionally reset or give user feedback
        console.warn('Date out of bounds:', newValue)
      }
    }
  }

  updateInput(value: any) {
    if (this.isInputsArrayTemplate) {
      this.inputValueChangedNotifier.emit({
        value: value,
        index: this.index,
      })
      return
    }

    if (this.isInputOptionsParentInput) return

    this.calculationModuleService.updateInputValue(this.cmInput, value)
  }
}
